import { Boxes, Building2, Network, Scale, ShieldCheck, UserCog } from 'lucide-react';
import type { TopicDefinition } from '@/shared/surfaces/TopicCatalogTypes';

/**
 * Governance surface catalog. Frames the same classified interaction data
 * through an oversight lens: is AI used for work, which (un)sanctioned models
 * are in play, how trustworthy the classification is, and how usage rolls up
 * the org hierarchy.
 */
export const GOVERNANCE_CATALOG: TopicDefinition[] = [
    {
        id: 'work-vs-personal',
        title: 'Work, or not?',
        hook: 'Not every prompt is on-task — see the split at a glance.',
        question: 'How much AI activity is work-related versus personal?',
        icon: Scale,
        accent: 'indigo',
        vizKind: 'donut',
        primaryField: 'work_related',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'work type',
        lensPlural: 'work types',
    },
    {
        id: 'team-work-split',
        title: 'On-task by team',
        hook: 'Some teams stay on-task; others wander.',
        question: 'How does the work-related split vary across departments?',
        icon: Building2,
        accent: 'teal',
        vizKind: 'matrix',
        primaryField: 'user_department',
        secondaryField: 'work_related',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'department',
        lensPlural: 'departments',
        matrixRowLimit: 8,
    },
    {
        id: 'models-in-use',
        title: 'What models are in play',
        hook: 'Shadow models show up here before they show up on a bill.',
        question: 'Which models are actually being used across the org?',
        icon: Boxes,
        accent: 'violet',
        vizKind: 'donut',
        primaryField: 'model',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'model',
        lensPlural: 'models',
    },
    {
        id: 'classification-trust',
        title: 'How sure is the classifier?',
        hook: 'Low-confidence categories are where oversight should look first.',
        question: 'How confident is the classification across task categories?',
        icon: ShieldCheck,
        accent: 'amber',
        vizKind: 'ranked-bars',
        primaryField: 'task_category',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'task category',
        lensPlural: 'task categories',
    },
    {
        id: 'manager-rollup',
        title: 'Usage by manager',
        hook: 'Accountability rolls up — so does AI usage.',
        question: 'Which managers’ orgs lean hardest on AI?',
        icon: UserCog,
        accent: 'rose',
        vizKind: 'ranked-bars',
        primaryField: 'user_manager',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'manager',
        lensPlural: 'managers',
    },
    {
        id: 'org-unit-usage',
        title: 'Usage across org units',
        hook: 'Adoption is uneven across the org chart.',
        question: 'How is AI activity distributed across org units?',
        icon: Network,
        accent: 'cyan',
        vizKind: 'ranked-bars',
        primaryField: 'user_org_unit',
        headlineMetric: 'turns',
        unitLabel: 'prompts',
        lensLabel: 'org unit',
        lensPlural: 'org units',
    },
];
